import MemberCard from 'components/home/members/card';
import type { BundleEntry, Patient } from 'fhir/r4';
import { usePatientSearch } from 'hooks/use-patient-search';
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Card,
  Input,
  Message,
  Button,
  Header,
  Container,
  Segment,
  Radio,
  Form,
  Dropdown,
  Modal,
} from 'semantic-ui-react';
import { normalizePhoneNumber } from 'utils';

type SearchMode = 'lookup' | 'search';
type SearchType = 'phone' | 'email';

const PatientSearch: React.FC = () => {
  const navigate = useNavigate();
  const {
    phone,
    email,
    searchType: _activeSearchTypeFromHook,
    searchMode,
    activeSearchType,
    autoRedirect: _autoRedirect,
    results,
    unparsedData,
    isLoading,
    error,
    updateSearchParams,
    clearSearch: _clearSearch,
  } = usePatientSearch();

  // Local state for email input (not synced to URL until search is clicked)
  const [emailInput, setEmailInput] = useState(email);

  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [finalConfirmModalOpen, setFinalConfirmModalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<BundleEntry & { resource?: Patient } | null>(null);

  useEffect(() => {
    if (email !== emailInput) {
      setEmailInput(email);
    }
  }, [email]);

  // Helper to format phone number as user types for display
  const formatPhoneInput = (digits: string): string => {
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const isValidEmail = (emailStr: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(emailStr.trim());
  };

  const normalizedPhone = normalizePhoneNumber(phone.trim());

  const getValidDigitCount = (normalized: string): number => {
    return normalized.startsWith('1') ? normalized.length - 1 : normalized.length;
  };

  const validDigitCount = getValidDigitCount(normalizedPhone);
  const isValidPhone = validDigitCount === 10;
  const isPartialPhone = validDigitCount > 0 && validDigitCount < 10;
  const isTooManyDigits = validDigitCount > 10;

  // Email validation for input field
  const trimmedEmailInput = emailInput.trim();
  const isValidEmailInput = isValidEmail(trimmedEmailInput);
  const isPartialEmailInput = trimmedEmailInput.length > 0 && !isValidEmailInput;

  // Check if we have results to display (based on URL params, not local input)
  const hasSearchedEmail = email.length > 0;
  const hasSearchedPhone = phone.length > 0;

  const handlePhoneSearch = (value: string): void => {
    // Phone searches automatically as user types
    updateSearchParams({ phone: value, email: '' });
  };

  const handleEmailInputChange = (value: string): void => {
    // Email just updates local state, doesn't trigger search
    setEmailInput(value);
  };

  const handleEmailSearch = (): void => {
    // Email searches only when button is clicked
    updateSearchParams({ email: emailInput, phone: '' });
  };

  const handleEmailKeyPress = (e: React.KeyboardEvent): void => {
    // Allow Enter key to trigger search
    if (e.key === 'Enter' && isValidEmailInput) {
      handleEmailSearch();
    }
  };

  const handleModeChange = (newMode: SearchMode): void => {
    const shouldRedirect = newMode === 'lookup';
    updateSearchParams({ shouldRedirect });
  };

  const handleSearchTypeChange = (newType: SearchType): void => {
    if (newType === 'email') {
      // When switching to email, clear email input
      setEmailInput('');
    }
    updateSearchParams({
      searchType: newType,
      phone: '',
      email: '',
    });
  };

  const handlePatientCardClick = (patient: BundleEntry & { resource?: Patient }): void => {
    setSelectedPatient(patient);
    setConfirmModalOpen(true);
  };

  const handleProceedClick = (): void => {
    // Close first modal and open second confirmation
    setConfirmModalOpen(false);
    setFinalConfirmModalOpen(true);
  };

  const handleFinalConfirm = (): void => {
    if (selectedPatient) {
      navigate(`/patients/${selectedPatient.resource!.id}`, { replace: true });
    }
    setFinalConfirmModalOpen(false);
    setSelectedPatient(null);
  };

  const handleCancelNavigation = (): void => {
    setConfirmModalOpen(false);
    setSelectedPatient(null);
  };

  const handleCancelFinalConfirm = (): void => {
    setFinalConfirmModalOpen(false);
    // Don't clear selectedPatient, go back to first modal
    setConfirmModalOpen(true);
  };

  const getCurrentSearchValue = (): string => {
    return activeSearchType === 'phone' ? phone : emailInput;
  };

  const getStatusMessage = () => {
    if (error) {
      return (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      );
    }

    // Phone-specific validation messages
    if (activeSearchType === 'phone') {
      if (isPartialPhone) {
        return (
          <Message warning>
            <Message.Header>Incomplete Phone Number</Message.Header>
            <p>Please enter a complete 10-digit phone number to search ({validDigitCount}/10 digits entered)</p>
          </Message>
        );
      }

      if (isTooManyDigits) {
        return (
          <Message warning>
            <Message.Header>Invalid Phone Number</Message.Header>
            <p>Phone number has too many digits ({validDigitCount}/10 digits). Please check and try again.</p>
          </Message>
        );
      }

      if (isValidPhone && hasSearchedPhone && results.length === 0 && !isLoading) {
        return (
          <Message>
            <Message.Header>No Results Found</Message.Header>
            <p>
              No patients found with phone number: <strong>{formatPhoneInput(normalizedPhone)}</strong>
            </p>
            <p style={{ color: '#999', fontSize: '14px' }}>
              Try a different phone number or check if the number is correct
            </p>
          </Message>
        );
      }
    }

    // Email-specific validation messages
    if (activeSearchType === 'email') {
      if (hasSearchedEmail && results.length === 0 && !isLoading) {
        return (
          <Message>
            <Message.Header>No Results Found</Message.Header>
            <p>
              No patients found with email: <strong>{email}</strong>
            </p>
            <p style={{ color: '#999', fontSize: '14px' }}>
              Try a different email address or check if the email is correct
            </p>
          </Message>
        );
      }
    }

    return null;
  };

  const getInputIcon = () => {
    if (activeSearchType === 'phone') {
      if (isValidPhone) return { name: 'check', color: 'green' };
      if (isPartialPhone) return 'warning sign';
      return 'phone';
    } else {
      if (isValidEmailInput) return { name: 'check', color: 'green' };
      if (isPartialEmailInput) return 'warning sign';
      return 'mail';
    }
  };

  const searchTypeOptions = [
    { key: 'phone', text: 'Phone Number', value: 'phone', icon: 'phone' },
    { key: 'email', text: 'Email Address', value: 'email', icon: 'mail' },
  ];

  return (
    <Container style={{ padding: '20px', width: '95%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Header as="h1" style={{ margin: 0 }}>
          Patient Search
        </Header>
        <Button as={Link} to="/" target="_blank" rel="noopener noreferrer" primary>
          Care Betty Home
        </Button>
      </div>

      <Segment>
        <Form>
          <Form.Group inline style={{ margin: '0' }}>
            <Form.Field>
              <Radio
                label={
                  <label>
                    <strong>Quick Lookup</strong>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      Auto-redirect if one patient found
                    </div>
                  </label>
                }
                value="lookup"
                checked={searchMode === 'lookup'}
                onChange={() => handleModeChange('lookup')}
              />
            </Form.Field>
            <Form.Field>
              <Radio
                label={
                  <label>
                    <strong>Search & Browse</strong>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>Show all matching results</div>
                  </label>
                }
                value="search"
                checked={searchMode === 'search'}
                onChange={() => handleModeChange('search')}
              />
            </Form.Field>
          </Form.Group>
        </Form>
      </Segment>

      <Form style={{ marginBottom: '20px' }}>
        <Form.Group widths="equal">
          <Form.Field width={4}>
            <label style={{ fontWeight: '500', color: '#333' }}>Search By</label>
            <Dropdown
              selection
              options={searchTypeOptions}
              value={activeSearchType}
              onChange={(_, data) => handleSearchTypeChange(data.value as SearchType)}
              disabled={isLoading}
              fluid
            />
          </Form.Field>
          <Form.Field width={12}>
            <label style={{ fontWeight: '500', color: '#333' }}>
              {activeSearchType === 'phone' ? 'Phone Number' : 'Email Address'}
              {activeSearchType === 'email' && (
                <span style={{ fontSize: '12px', color: '#666', fontWeight: 'normal', marginLeft: '8px' }}>
                  (Press Enter or click Search to find patients)
                </span>
              )}
            </label>
            <Input
              type={activeSearchType === 'phone' ? 'tel' : 'email'}
              value={getCurrentSearchValue()}
              onChange={(e) => {
                if (activeSearchType === 'phone') {
                  handlePhoneSearch(e.target.value);
                } else {
                  handleEmailInputChange(e.target.value);
                }
              }}
              onKeyPress={activeSearchType === 'email' ? handleEmailKeyPress : undefined}
              placeholder={activeSearchType === 'phone' ? '(555) 123-4567' : 'patient@example.com'}
              disabled={isLoading}
              fluid
              icon={getInputIcon()}
              iconPosition="left"
              error={activeSearchType === 'phone' ? isPartialPhone : isPartialEmailInput}
              loading={isLoading}
              action={
                activeSearchType === 'email' ? (
                  <Button
                    primary
                    disabled={!isValidEmailInput || isLoading}
                    loading={isLoading}
                    onClick={handleEmailSearch}
                    icon="search"
                    content="Search"
                  />
                ) : undefined
              }
            />
          </Form.Field>
        </Form.Group>
      </Form>

      {getStatusMessage()}

      {!isLoading && results.length > 0 && (
        <div>
          <Message success>
            <Message.Header>
              Found {results.length} patient{results.length !== 1 ? 's' : ''}
            </Message.Header>
            {searchMode === 'lookup' && results.length > 1 && <p>Multiple patients found - please select one below</p>}
          </Message>
          <Card.Group itemsPerRow={4}>
            {/*
              SUSPECTED DEFECT — `unparsedData` is the RAW bundle entries, not
              the filtered ones. `Bundle.entry.resource` is `0..1` in FHIR, and a
              `/Patient/?…` search can return a search-mode `outcome` entry that
              carries no `resource` — `use-patient-search.spec` pins exactly that
              case ("drops entries with no resource, such as a search-mode
              outcome"), but the drop happens in `parseFHIRSearchResponse`, which
              produces `results`, NOT `unparsedData`. So an outcome entry reaches
              this map and `patient.resource.id` throws, taking the results grid
              down after a search that did find patients.

              Both sides' assumptions: this consumer assumes every entry carries
              a resource, which `results` guarantees and `unparsedData` does not;
              the hook guarantees only `fhirData?.entry || []`.

              Reported, not fixed: filtering here would silently render fewer
              cards than the "Found N patients" count above claims.
            */}
            {unparsedData.map((patient) => (
              <MemberCard
                key={patient.resource!.id}
                // A `/Patient/?...` search, so every entry carries a `Patient`;
                // `BundleEntry.resource` is the whole `FhirResource` union.
                patient={patient.resource as Patient}
                onPatientSelect={() => handlePatientCardClick(patient as BundleEntry & { resource?: Patient })}
              />
            ))}
          </Card.Group>
        </div>
      )}

      {/* Empty State - No Search Yet */}
      {!isLoading && !hasSearchedPhone && !hasSearchedEmail && (
        <Message info>
          <Message.Header>Ready to search</Message.Header>
          <p>
            {activeSearchType === 'phone'
              ? 'Enter a phone number above to find patients'
              : 'Enter an email address and click Search to find patients'}
          </p>
        </Message>
      )}

      {/* First Confirmation Modal - Patient Preview */}
      <Modal open={confirmModalOpen} onClose={handleCancelNavigation} size="small">
        <Modal.Header>
          <div style={{ display: 'flex', alignItems: 'center' }}>Confirm Selection</div>
        </Modal.Header>
        <Modal.Content>
          {selectedPatient && (
            <div>
              <p style={{ fontSize: '16px', marginBottom: '20px', color: '#555' }}>
                Please confirm you want to view this patient's details:
              </p>
              <Card.Group itemsPerRow={1}>
                <MemberCard
                  patient={selectedPatient.resource!}
                  onPatientSelect={() => {}} // No-op since we're in preview mode
                />
              </Card.Group>
            </div>
          )}
        </Modal.Content>
        <Modal.Actions style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            as={Link}
            to={`/patients/${selectedPatient?.resource!.id}`}
            target="_blank"
            rel="noopener noreferrer"
            icon="external"
            labelPosition="left"
            content="Preview Member"
            size="large"
          />
          <div>
            <Button onClick={handleCancelNavigation} content="Cancel" size="large" />
            <Button
              primary
              onClick={handleProceedClick}
              icon="arrow right"
              labelPosition="right"
              content="Proceed"
              size="large"
            />
          </div>
        </Modal.Actions>
      </Modal>

      {/* Second Confirmation Modal - Final Confirmation */}
      <Modal open={finalConfirmModalOpen} onClose={handleCancelFinalConfirm} size="mini">
        <Modal.Header>Are you sure?</Modal.Header>
        <Modal.Content>
          <p>Do you want to proceed to this patient's page?</p>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCancelFinalConfirm} content="No" />
          <Button positive onClick={handleFinalConfirm} content="Yes" />
        </Modal.Actions>
      </Modal>
    </Container>
  );
};

export default PatientSearch;
